import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileText, Upload } from 'lucide-react'
import {
  ETICHETE_COD_PONTAJ,
  ETICHETE_STARE_CONCEDIU,
  ETICHETE_TIP_CONCEDIU,
  TIPURI_CONCEDIU,
  type StareConcediu,
  type TipConcediu,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { ETICHETE_PERIOADA, interval } from '../../lib/perioade'
import { DateRangeField } from '../../components/ui/DateField'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { CarduriSelector, type CardSelector } from '../../components/ui/CarduriSelector'
import { PanouLateral, SectiunePanou } from '../../components/ui/PanouLateral'
import { Pastila } from '../../components/ui/Pastila'
import { StareLista } from '../../components/ui/StareLista'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { useConfirmare } from '../../components/ui/Confirmare'
import type { Ton } from '../../components/ui/TonuriB4L'

/*
  Firmă & oameni → Concedii (Victor, 20.10.2026; macheta aprobata de Madalin aceeasi zi).

  Doar evidenta: cererile vin singure din mail (PDF-ul atasat), CRM-ul le citeste. Aici se vad
  pe luna — lista sau calendarul „cine lipseste cand" (ca la Timetastic / Personio) — iar ce n-a
  fost citit sigur se confirma sau se corecteaza dintr-un clic.
*/

interface Cerere {
  id: number
  om: { id: number; name: string } | null
  numeCitit: string
  potrivire: 'sigura' | 'propusa' | 'lipsa'
  tip: TipConcediu
  tipDetaliu: string | null
  dataStart: string | null
  dataFinal: string | null
  zileLucratoare: number | null
  zileScrise: number | null
  dataCererii: string | null
  semnata: boolean | null
  stare: StareConcediu
  motive: string[]
  pagina: number
  areDocument: boolean
  dinCorpulMailului?: boolean
  email: { deLa: string | null; subiect: string | null; primit: string | null }
  confirmat: { de: string; la: string } | null
  alteDinMail?: number
  /** Cererea scrisa direct in mail, fara document: textul mailului. */
  textMail?: string | null
}

/** O zi de concediu din pontajul zilnic al salarizarii (EA). */
interface ZiEfectuata {
  data: string
  numeEa: string
  om: { id: number; name: string } | null
  tip: TipConcediu
  cod: string
}

interface Raspuns {
  luna: string
  deLa: string
  panaLa: string
  azi: string
  cereri: Cerere[]
  efectuate: ZiEfectuata[]
  luniImportate: { luna: string; la: string }[]
  oameni: { id: number; name: string }[]
  verificare: {
    deLa: string | null
    faraCerere: { om: string; userId: number | null; zile: string[] }[]
    faraPontaj: { cerereId: number; om: string; zile: string[] }[]
  }
  nelucratoare: string[]
  rezumat: {
    azi: { ziLucratoare: boolean; oameni: { nume: string; tip: TipConcediu; panaLa: string }[] }
    luna: { cereri: number; zileLucratoare: number }
    urmatoare14: number
    deVerificat: number
  }
}

type Vedere = 'luna' | 'azi' | 'urmatoare' | 'verificat'

/** Culorile tipurilor: se deosebesc si prin luminozitate, nu doar prin nuanta. */
const TON_TIP: Record<TipConcediu, Ton> = {
  odihna: 'albastru',
  medical: 'rosu',
  fara_plata: 'mov',
  eveniment: 'verde',
  altele: 'gri',
}
const BANDA_TIP: Record<TipConcediu, string> = {
  odihna: 'bg-b4l-blue',
  medical: 'bg-b4l-red',
  fara_plata: 'bg-b4l-purple',
  eveniment: 'bg-b4l-green',
  altele: 'bg-b4l-text-dim',
}

const LUNI = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
]
const ZILE_SCURT = ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ']

const laData = (iso: string) => new Date(`${iso}T00:00:00Z`)
function dataScurta(iso: string | null): string {
  if (!iso) return '—'
  const d = laData(iso)
  return `${ZILE_SCURT[d.getUTCDay()]!.toLowerCase()} ${d.getUTCDate()} ${LUNI[d.getUTCMonth()]!.slice(0, 3)}.`
}
function perioada(c: Pick<Cerere, 'dataStart' | 'dataFinal'>): string {
  if (!c.dataStart || !c.dataFinal) return 'perioadă necitită'
  if (c.dataStart === c.dataFinal) return dataScurta(c.dataStart)
  return `${dataScurta(c.dataStart)} – ${dataScurta(c.dataFinal)}`
}
const numeLuna = (luna: string) => {
  const [a, l] = luna.split('-').map(Number) as [number, number]
  return `${LUNI[l - 1]![0]!.toUpperCase()}${LUNI[l - 1]!.slice(1)} ${a}`
}
/** Lunile (`AAAA-LL`) dintre doua zile, ambele incluse. */
function luniIntre(deLa: string, panaLa: string): string[] {
  const out: string[] = []
  let [a, l] = deLa.slice(0, 7).split('-').map(Number) as [number, number]
  const sf = panaLa.slice(0, 7)
  for (let i = 0; i < 48; i++) {
    const k = `${a}-${String(l).padStart(2, '0')}`
    out.push(k)
    if (k >= sf) break
    l++
    if (l > 12) {
      l = 1
      a++
    }
  }
  return out
}
const cheieOm = (om: { id: number } | null, nume: string) => (om ? `u${om.id}` : `n${nume}`)
const zileText = (zile: string[]) =>
  zile.length <= 4
    ? zile.map((z) => dataScurta(z)).join(', ')
    : `${zile.length} zile, ${dataScurta(zile[0]!)} – ${dataScurta(zile[zile.length - 1]!)}`
const eticheta = (c: Cerere) =>
  c.tip === 'altele' && c.tipDetaliu ? `Altele: ${c.tipDetaliu}` : ETICHETE_TIP_CONCEDIU[c.tip]
const eroare = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? 'Nu s-a putut salva.'

export function ConcediiPage() {
  const qc = useQueryClient()
  const poateScrie = useAuthStore((s) => s.permissions.includes('hr.concedii:write'))
  const [param, setParam] = useSearchParams()
  const mod = param.get('mod') === 'calendar' ? 'calendar' : 'lista'
  /*
    Perioada, ca pe celelalte ecrane cu date (Alexandru, 20.10.2026): luna curenta, luna
    precedenta, anul curent, anul precedent, sau orice interval. In URL, ca sa poata fi trimisa.
  */
  const implicit = useMemo(() => interval('luna_curenta'), [])
  const deLa = param.get('de_la') ?? implicit.from
  const panaLa = param.get('pana_la') ?? implicit.to
  const seteazaPerioada = (de: string, pana: string) => {
    const p = new URLSearchParams(param)
    p.set('de_la', de)
    p.set('pana_la', pana)
    setParam(p, { replace: true })
    setVedere('luna')
  }
  const scurtaturi = useMemo(
    () =>
      (['luna_curenta', 'luna_precedenta', 'an_curent', 'an_precedent'] as const).map((n) => ({
        cheie: n,
        eticheta: ETICHETE_PERIOADA[n],
        ...interval(n),
      })),
    [],
  )
  const [vedere, setVedere] = useState<Vedere>('luna')
  const [importDeschis, setImportDeschis] = useState(false)
  const [om, setOm] = useState('')
  const [tip, setTip] = useState<'' | TipConcediu>('')
  const [deschisId, setDeschisId] = useState<number | null>(null)

  const q = useQuery<Raspuns>({
    queryKey: ['concedii', deLa, panaLa, vedere, om, tip],
    queryFn: async () =>
      (
        await api.get<Raspuns>('/concedii', {
          params: {
            de_la: deLa,
            pana_la: panaLa,
            ...(vedere !== 'luna' ? { vedere } : {}),
            ...(om ? { userId: om } : {}),
            ...(tip ? { tip } : {}),
          },
        })
      ).data,
  })
  const r = q.data?.rezumat
  const cereri = useMemo(() => q.data?.cereri ?? [], [q.data])
  const oSinguraLuna = deLa.slice(0, 7) === panaLa.slice(0, 7)

  /*
    Toti oamenii activi din CRM (Alexandru, 20.10.2026: „la Om ar trebui sa apara lista de
    angajati"). Pana atunci lista se facea din cererile gasite — intr-o luna fara cereri ramanea
    doar „Toata lumea".
  */
  const oameniLista = useMemo(
    () =>
      [...(q.data?.oameni ?? [])]
        .map((o) => [o.id, o.name] as const)
        .sort((a, b) => a[1].localeCompare(b[1], 'ro')),
    [q.data],
  )

  const carduri: CardSelector<Vedere>[] = [
    {
      cheie: 'luna',
      eticheta: oSinguraLuna ? `În ${LUNI[Number(deLa.slice(5, 7)) - 1]}` : 'În perioadă',
      valoare: r?.luna.cereri ?? '—',
      detaliu: r ? `cereri, ${r.luna.zileLucratoare} zile lucrătoare` : null,
      ton: 'albastru',
    },
    {
      cheie: 'azi',
      eticheta: 'Azi lipsesc',
      valoare: r?.azi.oameni.length ?? '—',
      detaliu: r
        ? r.azi.oameni.length
          ? r.azi.oameni.map((o) => o.nume).join(', ')
          : r.azi.ziLucratoare
            ? 'toată echipa e la lucru'
            : 'zi nelucrătoare'
        : null,
      ton: 'gri',
    },
    {
      cheie: 'urmatoare',
      eticheta: 'Următoarele 14 zile',
      valoare: r?.urmatoare14 ?? '—',
      detaliu: r
        ? r.urmatoare14 === 1
          ? 'coleg intră în concediu'
          : 'colegi intră în concediu'
        : null,
      ton: 'mov',
    },
    {
      cheie: 'verificat',
      eticheta: 'De verificat',
      valoare: r?.deVerificat ?? '—',
      detaliu: 'AI-ul n-a fost sigur de ceva',
      ton: r?.deVerificat ? 'galben' : 'gri',
    },
  ]

  const schimbaMod = (m: 'lista' | 'calendar') => {
    const p = new URLSearchParams(param)
    if (m === 'calendar') p.set('mod', 'calendar')
    else p.delete('mod')
    setParam(p, { replace: true })
    if (m === 'calendar') setVedere('luna')
  }

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Concedii"
        descriere="Cererile vin singure din mail (Aurora sau Victor în CC), cu PDF-ul atașat. Aici doar se țin în evidență."
        actiuni={
          <div
            role="tablist"
            aria-label="Vedere"
            className="flex gap-1 rounded-caseta border border-b4l-border bg-b4l-surface p-1"
          >
            {(['lista', 'calendar'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mod === m}
                onClick={() => schimbaMod(m)}
                className={`rounded-md px-3.5 py-1.5 text-corp ${mod === m ? 'bg-b4l-bg-soft font-semibold text-b4l-text' : 'text-b4l-text-muted hover:text-b4l-text'}`}
              >
                {m === 'lista' ? 'Listă' : 'Calendar'}
              </button>
            ))}
          </div>
        }
      />

      <CarduriSelector
        carduri={carduri}
        ales={vedere}
        onAlege={(k) => {
          setVedere(k)
          if (k !== 'luna' && mod === 'calendar') schimbaMod('lista')
        }}
        eticheta="Concediile, pe scurt"
      />

      <div className="flex flex-wrap items-center gap-2 text-corp">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Perioada">
          {scurtaturi.map((sc) => (
            <button
              key={sc.cheie}
              type="button"
              onClick={() => seteazaPerioada(sc.from, sc.to)}
              className={`rounded-caseta border px-3 py-1.5 ${
                vedere === 'luna' && sc.from === deLa && sc.to === panaLa
                  ? 'border-b4l-blue bg-b4l-blue font-medium text-white'
                  : 'border-b4l-border bg-b4l-surface text-b4l-text hover:border-b4l-blue'
              }`}
            >
              {sc.eticheta}
            </button>
          ))}
        </div>
        <div className="w-full sm:w-72">
          <DateRangeField
            fromValue={deLa}
            toValue={panaLa}
            onChange={(de, pana) => de && pana && seteazaPerioada(de, pana)}
          />
        </div>
        <label className="flex items-center gap-1.5 text-b4l-text-muted">
          Angajat
          <select
            value={om}
            onChange={(e) => setOm(e.target.value)}
            className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-b4l-text"
          >
            <option value="">Toți angajații</option>
            {oameniLista.map(([id, nume]) => (
              <option key={id} value={id}>
                {nume}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-b4l-text-muted">
          Tip
          <select
            value={tip}
            onChange={(e) => setTip(e.target.value as '' | TipConcediu)}
            className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1.5 text-b4l-text"
          >
            <option value="">Toate</option>
            {TIPURI_CONCEDIU.map((t) => (
              <option key={t} value={t}>
                {ETICHETE_TIP_CONCEDIU[t]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <StareLista
        interogare={q}
        gol={cereri.length === 0 && mod === 'lista'}
        mesajGol={
          vedere === 'verificat'
            ? 'Nimic de verificat. Tot ce a venit pe mail a fost citit sigur.'
            : vedere === 'azi'
              ? 'Azi nu lipsește nimeni.'
              : 'Nicio cerere de concediu în perioada asta. Ele apar singure, din mail, în cel mult 15 minute de la sosire.'
        }
      >
        {mod === 'calendar' && q.data ? (
          <div className="grid gap-5">
            {luniIntre(q.data.deLa, q.data.panaLa)
              .slice(0, 12)
              .map((l) => (
                <div key={l} className="grid gap-2">
                  {!oSinguraLuna && (
                    <h3 className="m-0 text-corp font-semibold text-b4l-text">{numeLuna(l)}</h3>
                  )}
                  <Calendar luna={l} date={q.data!} laDeschidere={setDeschisId} />
                </div>
              ))}
          </div>
        ) : (
          <Lista cereri={cereri} deschis={deschisId} laDeschidere={setDeschisId} />
        )}
      </StareLista>

      {mod === 'lista' && vedere === 'luna' && q.data && (
        <>
          <Efectuat date={q.data} poateScrie={poateScrie} laImport={() => setImportDeschis(true)} />
          <SoldConcediu an={Number(deLa.slice(0, 4))} poateScrie={poateScrie} />
          <Comparatie date={q.data} laDeschidere={setDeschisId} />
        </>
      )}

      {importDeschis && (
        <ImportPontaj
          laInchidere={() => setImportDeschis(false)}
          laImport={() => void qc.invalidateQueries({ queryKey: ['concedii'] })}
        />
      )}

      {deschisId != null && (
        <Panou
          id={deschisId}
          poateScrie={poateScrie}
          laInchidere={() => setDeschisId(null)}
          laSchimbare={() => void qc.invalidateQueries({ queryKey: ['concedii'] })}
        />
      )}
    </div>
  )
}

function Stare({ c }: { c: Pick<Cerere, 'stare'> }) {
  const ton: Ton =
    c.stare === 'inregistrata' ? 'verde' : c.stare === 'de_verificat' ? 'galben' : 'gri'
  return <Pastila ton={ton}>{ETICHETE_STARE_CONCEDIU[c.stare]}</Pastila>
}

function Lista({
  cereri,
  deschis,
  laDeschidere,
}: {
  cereri: Cerere[]
  deschis: number | null
  laDeschidere: (id: number) => void
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
      <table className="w-full min-w-[720px] border-collapse text-corp">
        <thead>
          <tr className="text-left text-mic text-b4l-text-muted">
            <th scope="col" className="border-b border-b4l-border px-3.5 py-2.5 font-medium">
              Angajat
            </th>
            <th scope="col" className="border-b border-b4l-border px-2 py-2.5 font-medium">
              Tip
            </th>
            <th scope="col" className="border-b border-b4l-border px-2 py-2.5 font-medium">
              Perioada
            </th>
            <th
              scope="col"
              className="border-b border-b4l-border px-2 py-2.5 text-right font-medium"
            >
              Zile lucr.
            </th>
            <th scope="col" className="border-b border-b4l-border px-2 py-2.5 font-medium">
              Primită
            </th>
            <th scope="col" className="border-b border-b4l-border px-3.5 py-2.5 font-medium">
              Stare
            </th>
          </tr>
        </thead>
        <tbody>
          {cereri.map((c) => (
            <tr
              key={c.id}
              className={`cursor-pointer hover:bg-b4l-bg-soft ${deschis === c.id ? 'bg-b4l-bg-soft' : ''}`}
              onClick={() => laDeschidere(c.id)}
            >
              <td className="border-b border-b4l-border px-3.5 py-2.5 font-semibold text-b4l-text">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    laDeschidere(c.id)
                  }}
                  className="text-left hover:underline"
                >
                  {c.om?.name ?? `${c.numeCitit} ?`}
                </button>
              </td>
              <td className="border-b border-b4l-border px-2 py-2.5">
                <Pastila ton={TON_TIP[c.tip]}>{eticheta(c)}</Pastila>
              </td>
              <td className="border-b border-b4l-border px-2 py-2.5 text-b4l-text">
                {perioada(c)}
              </td>
              <td className="border-b border-b4l-border px-2 py-2.5 text-right tabular-nums">
                {c.zileLucratoare ?? '—'}
              </td>
              <td className="border-b border-b4l-border px-2 py-2.5 text-b4l-text-muted">
                {c.email.primit ? dataScurta(c.email.primit.slice(0, 10)) : '—'}
              </td>
              <td className="border-b border-b4l-border px-3.5 py-2.5">
                <Stare c={c} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="m-0 px-3.5 py-2.5 text-mic text-b4l-text-dim">
        Zilele lucrătoare le socotește CRM-ul: fără weekend și fără sărbătorile legale.
      </p>
    </div>
  )
}

/**
 * „Cine lipseste cand": un rand pe om, o coloana pe zi. Weekendurile si sarbatorile estompate,
 * azi evidentiat; benzile pe tip. Cele de verificat — contur punctat, ca sa se vada ca nu-s sigure.
 */
function Calendar({
  luna,
  date,
  laDeschidere,
}: {
  luna: string
  date: Raspuns
  laDeschidere: (id: number) => void
}) {
  const [an, l] = luna.split('-').map(Number) as [number, number]
  const nrZile = new Date(Date.UTC(an, l, 0)).getUTCDate()
  const zile = Array.from({ length: nrZile }, (_, i) => {
    const iso = `${luna}-${String(i + 1).padStart(2, '0')}`
    return {
      iso,
      nr: i + 1,
      sapt: ZILE_SCURT[laData(iso).getUTCDay()]!,
      libera: date.nelucratoare.includes(iso),
      azi: iso === date.azi,
    }
  })
  const prima = `${luna}-01`
  const ultima = `${luna}-${String(nrZile).padStart(2, '0')}`
  const cuPerioada = useMemo(
    () =>
      date.cereri.filter(
        (c) => c.dataStart && c.dataFinal && c.dataStart <= ultima && c.dataFinal >= prima,
      ),
    [date.cereri, prima, ultima],
  )
  /*
    Randurile: oamenii cu cereri SAU cu zile in pontaj in luna asta. Ziua din pontaj fara cerere
    apare estompata — e platita ca concediu, dar n-a venit pe mail.
  */
  const randuri = useMemo(() => {
    const m = new Map<
      string,
      { nume: string; cereri: Cerere[]; efectuate: Map<string, ZiEfectuata> }
    >()
    const rand = (k: string, nume: string) => {
      const r = m.get(k) ?? {
        nume,
        cereri: [] as Cerere[],
        efectuate: new Map<string, ZiEfectuata>(),
      }
      m.set(k, r)
      return r
    }
    for (const c of cuPerioada)
      rand(cheieOm(c.om, c.numeCitit), c.om?.name ?? `${c.numeCitit} ?`).cereri.push(c)
    for (const e of date.efectuate)
      if (e.data.startsWith(luna))
        rand(cheieOm(e.om, e.numeEa), e.om?.name ?? e.numeEa).efectuate.set(e.data, e)
    return [...m.values()].sort((a, b) => a.nume.localeCompare(b.nume, 'ro'))
  }, [cuPerioada, date.efectuate, luna])
  const coloane = `minmax(150px,180px) repeat(${nrZile}, minmax(22px, 1fr))`

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap gap-3.5 text-mic text-b4l-text-muted" aria-label="Legendă">
        {TIPURI_CONCEDIU.map((t) => (
          <span key={t} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-3.5 rounded-sm ${BANDA_TIP[t]}`} aria-hidden />
            {ETICHETE_TIP_CONCEDIU[t]}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-3.5 rounded-sm border border-dashed border-b4l-amber"
            aria-hidden
          />
          De verificat
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-3.5 rounded-sm bg-b4l-blue opacity-40" aria-hidden />
          În pontaj, fără cerere
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
        <div className="min-w-[900px]">
          <div
            className="grid border-b border-b4l-border text-eticheta text-b4l-text-muted"
            style={{ gridTemplateColumns: coloane }}
          >
            <div className="px-3 py-2 text-mic font-semibold">Angajat</div>
            {zile.map((z) => (
              <div
                key={z.iso}
                className={`flex flex-col items-center border-l border-b4l-border py-1.5 ${z.azi ? 'bg-b4l-blue-light text-b4l-blue-dark' : z.libera ? 'bg-b4l-bg-soft text-b4l-text-dim' : ''}`}
              >
                <span>{z.sapt}</span>
                <span className="text-mic font-semibold">{z.nr}</span>
              </div>
            ))}
          </div>
          {randuri.length === 0 && (
            <p className="m-0 px-3 py-6 text-center text-corp text-b4l-text-muted">
              Nimeni nu lipsește în {LUNI[l - 1]}.
            </p>
          )}
          {randuri.map((r) => (
            <div
              key={r.nume}
              className="grid h-11 border-b border-b4l-border"
              style={{ gridTemplateColumns: coloane }}
            >
              <div className="flex items-center truncate px-3 text-corp font-medium text-b4l-text">
                {r.nume}
              </div>
              {zile.map((z) => {
                const c = r.cereri.find((x) => x.dataStart! <= z.iso && x.dataFinal! >= z.iso)
                const inceput = c?.dataStart === z.iso
                const sfarsit = c?.dataFinal === z.iso
                const e = c ? undefined : r.efectuate.get(z.iso)
                return (
                  <div
                    key={z.iso}
                    className={`flex items-center border-l border-b4l-border ${z.azi ? 'bg-b4l-blue-light/40' : z.libera ? 'bg-b4l-bg-soft' : ''}`}
                  >
                    {c && !z.libera && (
                      <button
                        type="button"
                        onClick={() => laDeschidere(c.id)}
                        title={`${r.nume} · ${eticheta(c)} · ${perioada(c)}`}
                        aria-label={`${r.nume}, ${eticheta(c)}, ${perioada(c)}`}
                        className={`h-5 w-full ${c.stare === 'de_verificat' ? 'border border-dashed border-b4l-amber bg-transparent' : BANDA_TIP[c.tip]} ${inceput ? 'ml-0.5 rounded-l' : ''} ${sfarsit ? 'mr-0.5 rounded-r' : ''}`}
                      />
                    )}
                    {e && (
                      <span
                        title={`${r.nume} · în pontaj: ${eticheteCod(e)} · fără cerere pe mail`}
                        aria-label={`${r.nume}, în pontaj ${eticheteCod(e)}, fără cerere`}
                        className={`mx-0.5 h-5 w-full rounded opacity-40 ${BANDA_TIP[e.tip]}`}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      <p className="m-0 text-mic text-b4l-text-dim">
        Weekendurile și sărbătorile legale sunt estompate și nu intră la zile lucrătoare. Coloana
        albastră e ziua de azi. Clic pe o bandă: cererea, cu documentul. Benzile pale sunt zile din
        pontajul de salarizare pentru care nu există o cerere pe mail.
      </p>
    </div>
  )
}

const eticheteCod = (e: Pick<ZiEfectuata, 'tip' | 'cod'>) =>
  e.tip === 'altele'
    ? `Altele: ${ETICHETE_COD_PONTAJ[e.cod] ?? e.cod}`
    : ETICHETE_TIP_CONCEDIU[e.tip]

/**
 * Concediile EFECTUATE in perioada, din pontajul zilnic al salarizarii (EA): zile pe om si pe tip.
 * E ce s-a platit; cererile de mai sus sunt ce s-a cerut.
 */
function Efectuat({
  date,
  poateScrie,
  laImport,
}: {
  date: Raspuns
  poateScrie: boolean
  laImport: () => void
}) {
  const tipuri = TIPURI_CONCEDIU.filter((t) => t !== 'eveniment')
  const randuri = useMemo(() => {
    const m = new Map<
      string,
      { nume: string; pe: Partial<Record<TipConcediu, number>>; total: number }
    >()
    for (const e of date.efectuate) {
      const k = cheieOm(e.om, e.numeEa)
      const r = m.get(k) ?? { nume: e.om?.name ?? `${e.numeEa} ?`, pe: {}, total: 0 }
      r.pe[e.tip] = (r.pe[e.tip] ?? 0) + 1
      r.total++
      m.set(k, r)
    }
    return [...m.values()].sort((a, b) => a.nume.localeCompare(b.nume, 'ro'))
  }, [date.efectuate])
  const luni = date.luniImportate.filter(
    (l) => l.luna >= date.deLa.slice(0, 7) && l.luna <= date.panaLa.slice(0, 7),
  )

  return (
    <section className="grid gap-2" aria-labelledby="titlu-efectuat">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="titlu-efectuat" className="m-0 text-titlu font-semibold text-b4l-text">
            Efectuat, din pontajul de salarizare
          </h2>
          <p className="m-0 text-mic text-b4l-text-muted">
            {luni.length
              ? `Zile din pontajul zilnic ExpertAccounts, pe ${luni.map((l) => numeLuna(l.luna).toLowerCase()).join(', ')}.`
              : 'Nicio lună din perioada asta n-are pontajul importat.'}
          </p>
        </div>
        {poateScrie && (
          <Button variant="secondary" onClick={laImport}>
            <Upload size={14} className="mr-1.5" />
            Importă pontajul unei luni
          </Button>
        )}
      </div>
      {randuri.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
          <table className="w-full min-w-[560px] border-collapse text-corp">
            <thead>
              <tr className="text-left text-mic text-b4l-text-muted">
                <th scope="col" className="border-b border-b4l-border px-3.5 py-2.5 font-medium">
                  Angajat
                </th>
                {tipuri.map((t) => (
                  <th
                    key={t}
                    scope="col"
                    className="border-b border-b4l-border px-2 py-2.5 text-right font-medium"
                  >
                    {ETICHETE_TIP_CONCEDIU[t]}
                  </th>
                ))}
                <th
                  scope="col"
                  className="border-b border-b4l-border px-3.5 py-2.5 text-right font-medium"
                >
                  Total zile
                </th>
              </tr>
            </thead>
            <tbody>
              {randuri.map((r) => (
                <tr key={r.nume}>
                  <td className="border-b border-b4l-border px-3.5 py-2 font-medium text-b4l-text">
                    {r.nume}
                  </td>
                  {tipuri.map((t) => (
                    <td
                      key={t}
                      className="border-b border-b4l-border px-2 py-2 text-right tabular-nums"
                    >
                      {r.pe[t] ?? '—'}
                    </td>
                  ))}
                  <td className="border-b border-b4l-border px-3.5 py-2 text-right font-semibold tabular-nums">
                    {r.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="m-0 px-3.5 py-2.5 text-mic text-b4l-text-dim">
            Un nume urmat de „?” n-are cont în CRM. „Altele” înseamnă maternitate, suspendare sau
            zile libere plătite.
          </p>
        </div>
      )}
    </section>
  )
}

/**
 * Cererile din mail fata de pontaj (Alexandru, 20.10.2026: „ar trebui sa se coreleze"). Doar de
 * cand exista cereri in CRM si doar in lunile cu pontaj importat.
 */
interface RandSold {
  id: number
  numeEa: string
  om: { id: number; name: string } | null
  dataInceput: string
  dataSfarsit: string
  zileAn: number
  reportat: number | null
  zileFaraDrept: number
  cuvenitLaZi: number
  dreptAn: number
  efectuat: number
  ramasLaZi: number | null
  ramasAn: number | null
  nota: string | null
}

/**
 * Soldul concediului de odihna pe an (21.10.2026), ca in „Concedii de odihna_2026.xlsx":
 * reportat + cuvenit − efectuat. Efectuatul vine din pontajul importat; dreptul se tine aici.
 */
function SoldConcediu({ an, poateScrie }: { an: number; poateScrie: boolean }) {
  const qc = useQueryClient()
  const q = useQuery<{ an: number; randuri: RandSold[] }>({
    queryKey: ['concedii-sold', an],
    queryFn: async () => (await api.get('/concedii/sold', { params: { an } })).data,
  })
  const [editez, setEditez] = useState<Partial<RandSold> | null>(null)
  const genereaza = useMutation({
    mutationFn: async () => (await api.post('/concedii/sold/genereaza', { an })).data,
    onSuccess: (r: { create: number }) => {
      toast.success(
        r.create
          ? `${r.create} angajați trecuți pe ${an}, cu soldul din ${an - 1}.`
          : `Nimic de trecut din ${an - 1}.`,
      )
      void qc.invalidateQueries({ queryKey: ['concedii-sold'] })
    },
    onError: (e) => toast.error(eroare(e)),
  })
  const randuri = q.data?.randuri ?? []
  const nr = (n: number | null) => (n == null ? '—' : String(n))
  const th = 'border-b border-b4l-border px-2 py-2.5 text-right font-medium'
  const td = 'border-b border-b4l-border px-2 py-2 text-right tabular-nums'

  return (
    <section className="grid gap-2" aria-labelledby="titlu-sold">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="titlu-sold" className="m-0 text-titlu font-semibold text-b4l-text">
            Sold concediu de odihnă — {an}
          </h2>
          <p className="m-0 text-mic text-b4l-text-muted">
            Rămas = reportat din {an - 1} + cuvenit − efectuat. Efectuatul vine din pontajul de
            salarizare; zilele fără plată și de suspendare nu dau drept la concediu.
          </p>
        </div>
        {poateScrie && (
          <div className="flex gap-2">
            {randuri.length === 0 && (
              <Button
                variant="secondary"
                onClick={() => genereaza.mutate()}
                loading={genereaza.isPending}
              >
                Preia angajații din {an - 1}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setEditez({ zileAn: 21 })}>
              Adaugă angajat
            </Button>
          </div>
        )}
      </div>
      {randuri.length === 0 ? (
        <p className="m-0 rounded-xl border border-b4l-border bg-b4l-surface px-3.5 py-3 text-corp text-b4l-text-muted">
          Niciun angajat cu drept de concediu trecut pe {an}.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
          <table className="w-full min-w-[860px] border-collapse text-corp">
            <thead>
              <tr className="text-left text-mic text-b4l-text-muted">
                <th scope="col" className="border-b border-b4l-border px-3.5 py-2.5 font-medium">
                  Angajat
                </th>
                <th scope="col" className="border-b border-b4l-border px-2 py-2.5 font-medium">
                  Perioada
                </th>
                <th scope="col" className={th}>
                  Zile/an
                </th>
                <th scope="col" className={th}>
                  Reportat
                </th>
                <th scope="col" className={th}>
                  Cuvenit la zi
                </th>
                <th scope="col" className={th}>
                  Drept pe an
                </th>
                <th scope="col" className={th}>
                  Efectuat
                </th>
                <th scope="col" className={th}>
                  Rămas la zi
                </th>
                <th scope="col" className={`${th} pr-3.5`}>
                  Rămas pe an
                </th>
              </tr>
            </thead>
            <tbody>
              {randuri.map((r) => (
                <tr
                  key={r.id}
                  className={poateScrie ? 'cursor-pointer hover:bg-b4l-bg-soft' : ''}
                  onClick={() => poateScrie && setEditez(r)}
                  title={r.nota ?? undefined}
                >
                  <td className="border-b border-b4l-border px-3.5 py-2 font-medium text-b4l-text">
                    {r.om?.name ?? `${r.numeEa} ?`}
                    {r.nota && <span className="ml-1 text-b4l-text-dim">*</span>}
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2 text-b4l-text-muted">
                    {dataScurta(r.dataInceput)} – {dataScurta(r.dataSfarsit)}
                  </td>
                  <td className={td}>{r.zileAn}</td>
                  <td className={td}>
                    {r.reportat == null ? (
                      <span className="text-b4l-amber-dark">de completat</span>
                    ) : (
                      r.reportat
                    )}
                  </td>
                  <td className={td}>{r.cuvenitLaZi}</td>
                  <td className={td}>{r.dreptAn}</td>
                  <td className={td}>{r.efectuat}</td>
                  <td
                    className={`${td} font-semibold ${r.ramasLaZi != null && r.ramasLaZi < 0 ? 'text-b4l-red' : ''}`}
                  >
                    {nr(r.ramasLaZi)}
                  </td>
                  <td className={`${td} pr-3.5 font-semibold`}>{nr(r.ramasAn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="m-0 px-3.5 py-2.5 text-mic text-b4l-text-dim">
            Rămas la zi negativ = concediu luat în avans. „*” are o notă — treci cu mouse-ul peste
            rând. {poateScrie ? 'Clic pe rând: modifici dreptul.' : ''}
          </p>
        </div>
      )}
      {editez && (
        <EditeazaDrept
          an={an}
          r={editez}
          laInchidere={() => setEditez(null)}
          laSalvare={() => void qc.invalidateQueries({ queryKey: ['concedii-sold'] })}
        />
      )}
    </section>
  )
}

function EditeazaDrept({
  an,
  r,
  laInchidere,
  laSalvare,
}: {
  an: number
  r: Partial<RandSold>
  laInchidere: () => void
  laSalvare: () => void
}) {
  const nou = r.id == null
  const [numeEa, setNumeEa] = useState(r.numeEa ?? '')
  const [inceput, setInceput] = useState(
    r.dataInceput && r.dataInceput !== `${an}-01-01` ? r.dataInceput : '',
  )
  const [sfarsit, setSfarsit] = useState(
    r.dataSfarsit && r.dataSfarsit !== `${an}-12-31` ? r.dataSfarsit : '',
  )
  const [zileAn, setZileAn] = useState(String(r.zileAn ?? 21))
  const [reportat, setReportat] = useState(r.reportat == null ? '' : String(r.reportat))
  const [nota, setNota] = useState(r.nota ?? '')
  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.put('/concedii/sold', {
          an,
          numeEa,
          dataInceput: inceput || null,
          dataSfarsit: sfarsit || null,
          zileAn: Number(zileAn),
          reportat: reportat.trim() === '' ? null : Number(reportat),
          nota: nota.trim() || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Dreptul de concediu a fost salvat.')
      laSalvare()
      laInchidere()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  const camp = 'rounded-caseta border border-b4l-border px-2 py-1.5'
  return (
    <Modal
      open
      onClose={laInchidere}
      title={
        nou
          ? `Drept de concediu ${an} — angajat nou`
          : `Drept de concediu ${an} — ${r.om?.name ?? r.numeEa}`
      }
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button
            onClick={() => salveaza.mutate()}
            loading={salveaza.isPending}
            disabled={!numeEa.trim()}
          >
            Salvează
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-corp">
        <label className="grid gap-1">
          <span className="text-mic text-b4l-text-muted">
            Numele, ca în ExpertAccounts (Nume Prenume)
          </span>
          <input
            value={numeEa}
            onChange={(e) => setNumeEa(e.target.value)}
            disabled={!nou}
            className={camp}
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="text-mic text-b4l-text-muted">Angajat de la (gol = 1 ianuarie)</span>
            <input
              type="date"
              value={inceput}
              onChange={(e) => setInceput(e.target.value)}
              className={camp}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-mic text-b4l-text-muted">Plecat la (gol = încă angajat)</span>
            <input
              type="date"
              value={sfarsit}
              onChange={(e) => setSfarsit(e.target.value)}
              className={camp}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-mic text-b4l-text-muted">Zile de concediu pe an</span>
            <input
              inputMode="numeric"
              value={zileAn}
              onChange={(e) => setZileAn(e.target.value.replace(/[^0-9]/g, ''))}
              className={camp}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-mic text-b4l-text-muted">
              Reportat din {an - 1} (gol = necunoscut)
            </span>
            <input
              inputMode="numeric"
              value={reportat}
              onChange={(e) => setReportat(e.target.value.replace(/[^0-9-]/g, ''))}
              className={camp}
            />
          </label>
        </div>
        <label className="grid gap-1">
          <span className="text-mic text-b4l-text-muted">Notă</span>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={2}
            className={camp}
          />
        </label>
      </div>
    </Modal>
  )
}

function Comparatie({ date, laDeschidere }: { date: Raspuns; laDeschidere: (id: number) => void }) {
  const v = date.verificare
  if (!v.deLa || !date.luniImportate.length) return null
  const totul = v.faraCerere.length === 0 && v.faraPontaj.length === 0
  return (
    <section className="grid gap-2" aria-labelledby="titlu-comparatie">
      <div>
        <h2 id="titlu-comparatie" className="m-0 text-titlu font-semibold text-b4l-text">
          Cereri față de pontaj
        </h2>
        <p className="m-0 text-mic text-b4l-text-muted">
          De la {dataScurta(v.deLa)}, prima cerere primită pe mail, și doar în lunile cu pontaj
          importat.
        </p>
      </div>
      {totul ? (
        <p className="m-0 rounded-xl border border-b4l-border bg-b4l-surface px-3.5 py-3 text-corp text-b4l-text">
          Se potrivesc: fiecare zi de concediu din pontaj are cerere, și fiecare cerere apare în
          pontaj.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
            <h3 className="m-0 text-corp font-semibold text-b4l-text">
              În pontaj, fără cerere pe mail ({v.faraCerere.length})
            </h3>
            {v.faraCerere.length === 0 ? (
              <p className="m-0 mt-1 text-mic text-b4l-text-muted">Niciuna.</p>
            ) : (
              <ul className="m-0 mt-1.5 grid gap-1 p-0 text-corp">
                {v.faraCerere.map((x) => (
                  <li key={x.om} className="list-none">
                    <span className="font-medium">{x.om}</span>
                    <span className="text-b4l-text-muted"> — {zileText(x.zile)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-b4l-border bg-b4l-surface p-3.5">
            <h3 className="m-0 text-corp font-semibold text-b4l-text">
              Cerere pe mail, dar în pontaj e zi lucrată ({v.faraPontaj.length})
            </h3>
            {v.faraPontaj.length === 0 ? (
              <p className="m-0 mt-1 text-mic text-b4l-text-muted">Niciuna.</p>
            ) : (
              <ul className="m-0 mt-1.5 grid gap-1 p-0 text-corp">
                {v.faraPontaj.map((x) => (
                  <li key={x.cerereId} className="list-none">
                    <button
                      type="button"
                      onClick={() => laDeschidere(x.cerereId)}
                      className="text-left hover:underline"
                    >
                      <span className="font-medium">{x.om}</span>
                      <span className="text-b4l-text-muted"> — {zileText(x.zile)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

/** Importul pontajului zilnic al unei luni, exportat din EA. Inlocuieste luna intreaga. */
function ImportPontaj({
  laInchidere,
  laImport,
}: {
  laInchidere: () => void
  laImport: () => void
}) {
  const precedenta = interval('luna_precedenta').from.slice(0, 7)
  const [luna, setLuna] = useState(precedenta)
  const [fisier, setFisier] = useState<File | null>(null)
  const trimite = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append('luna', luna)
      fd.append('fisier', fisier!)
      return (
        await api.post<{
          rezultat: { zile: number; oameni: number; nelegati: string[] }
        }>('/concedii/efectuate/import', fd, { timeout: 60_000 })
      ).data.rezultat
    },
    onSuccess: (r) => {
      toast.success(
        `${numeLuna(luna)}: ${r.zile} zile de concediu, din pontajul a ${r.oameni} oameni.` +
          (r.nelegati.length ? ` Fără cont în CRM: ${r.nelegati.join(', ')}.` : ''),
      )
      laImport()
      laInchidere()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  return (
    <Modal
      open
      onClose={laInchidere}
      title="Importă pontajul unei luni"
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button
            onClick={() => trimite.mutate()}
            disabled={!fisier || !luna || trimite.isPending}
            loading={trimite.isPending}
          >
            Importă
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-corp">
        <p className="m-0 text-b4l-text-muted">
          Fișierul e exportul grilei <strong>Pontaj zilnic</strong> din ExpertAccounts, pe luna
          aleasă: coloanele <code>name</code>, <code>firstname</code> și <code>z1</code>…
          <code>z31</code>. Un import nou pe aceeași lună o înlocuiește pe cea veche.
        </p>
        <label className="grid gap-1">
          <span className="text-mic text-b4l-text-muted">Luna</span>
          <input
            type="month"
            value={luna}
            onChange={(e) => setLuna(e.target.value)}
            className="rounded-caseta border border-b4l-border px-2 py-1.5"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-mic text-b4l-text-muted">Fișierul (.xlsx sau .xls)</span>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFisier(e.target.files?.[0] ?? null)}
          />
        </label>
      </div>
    </Modal>
  )
}

function Panou({
  id,
  poateScrie,
  laInchidere,
  laSchimbare,
}: {
  id: number
  poateScrie: boolean
  laInchidere: () => void
  laSchimbare: () => void
}) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [corectez, setCorectez] = useState(false)
  const [altOm, setAltOm] = useState(false)
  const q = useQuery<{ cerere: Cerere }>({
    queryKey: ['concedii', 'cerere', id],
    queryFn: async () => (await api.get(`/concedii/${id}`)).data,
  })
  const c = q.data?.cerere
  const salveaza = useMutation({
    mutationFn: async (corp: Record<string, unknown>) =>
      (await api.patch<{ cerere: Cerere }>(`/concedii/${id}`, corp)).data,
    onSuccess: (r) => {
      qc.setQueryData(['concedii', 'cerere', id], { cerere: { ...c, ...r.cerere } })
      laSchimbare()
      setCorectez(false)
      setAltOm(false)
      toast.success(
        r.cerere.stare === 'nu_e_cerere'
          ? 'Scoasă din evidență.'
          : r.cerere.stare === 'inregistrata'
            ? 'Cererea e înregistrată.'
            : 'Salvat.',
      )
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const deschideDocumentul = async () => {
    try {
      const r = await api.get(`/concedii/${id}/document`, { responseType: 'blob', timeout: 60_000 })
      window.open(URL.createObjectURL(r.data as Blob), '_blank', 'noopener')
    } catch {
      toast.error('Documentul nu se poate deschide.')
    }
  }

  return (
    <PanouLateral
      deschis
      onInchide={laInchidere}
      titlu={c ? `${eticheta(c)} · ${perioada(c)}` : 'Cererea'}
      subtitlu={c ? <Stare c={c} /> : null}
    >
      {!c ? (
        <p className="text-corp text-b4l-text-muted">Se încarcă…</p>
      ) : (
        <div className="grid gap-4 text-corp">
          {c.stare === 'de_verificat' && (
            <div className="grid gap-2 rounded-caseta border border-b4l-amber/40 bg-b4l-amber-light p-3 text-b4l-amber-dark">
              <b className="font-semibold">De verificat</b>
              <ul className="m-0 grid list-disc gap-1 pl-4">
                {c.motive.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              {poateScrie && (
                <div className="flex flex-wrap gap-2">
                  {c.om && c.dataStart && (
                    <Button
                      onClick={() => salveaza.mutate({ stare: 'inregistrata' })}
                      loading={salveaza.isPending}
                    >
                      {c.potrivire === 'sigura' ? 'E în regulă' : `Confirmă: ${c.om.name}`}
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => setAltOm(true)}>
                    {c.om ? 'Alt angajat…' : 'Alege angajatul…'}
                  </Button>
                </div>
              )}
            </div>
          )}

          <dl className="m-0 grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-2">
            <dt className="text-b4l-text-muted">Angajat</dt>
            <dd className="m-0">
              {c.om?.name ?? '—'}
              {c.numeCitit && c.om && c.numeCitit.toLowerCase() !== c.om.name.toLowerCase() && (
                <span className="text-b4l-text-dim"> · pe cerere: „{c.numeCitit}”</span>
              )}
            </dd>
            <dt className="text-b4l-text-muted">Tip</dt>
            <dd className="m-0">{eticheta(c)}</dd>
            <dt className="text-b4l-text-muted">Perioada</dt>
            <dd className="m-0">
              {perioada(c)}
              {c.zileLucratoare != null &&
                ` · ${c.zileLucratoare} ${c.zileLucratoare === 1 ? 'zi lucrătoare' : 'zile lucrătoare'}`}
            </dd>
            <dt className="text-b4l-text-muted">Data cererii</dt>
            <dd className="m-0">{dataScurta(c.dataCererii)}</dd>
            <dt className="text-b4l-text-muted">Semnată</dt>
            <dd className="m-0">{c.semnata == null ? '—' : c.semnata ? 'da' : 'nu pare'}</dd>
            <dt className="text-b4l-text-muted">Din mail</dt>
            <dd className="m-0 break-words">
              {c.email.subiect ? `„${c.email.subiect}”` : '—'}
              {c.email.deLa && <span className="text-b4l-text-dim"> · de la {c.email.deLa}</span>}
            </dd>
            {c.confirmat && (
              <>
                <dt className="text-b4l-text-muted">Confirmată</dt>
                <dd className="m-0">
                  de {c.confirmat.de}, {new Date(c.confirmat.la).toLocaleDateString('ro-RO')}
                </dd>
              </>
            )}
          </dl>

          {c.areDocument && (
            <button
              type="button"
              onClick={() => void deschideDocumentul()}
              className="flex items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-bg-soft px-3 py-3 text-left text-b4l-text hover:border-b4l-blue"
            >
              <FileText size={18} className="text-b4l-text-muted" aria-hidden />
              <span>
                Deschide documentul
                <span className="block text-mic text-b4l-text-dim">
                  pagina {c.pagina}
                  {c.alteDinMail
                    ? ` · același mail mai avea ${c.alteDinMail} ${c.alteDinMail === 1 ? 'cerere' : 'cereri'}, înregistrate separat`
                    : ''}
                </span>
              </span>
            </button>
          )}

          {c.textMail != null && (
            <SectiunePanou titlu="Cererea, scrisă în mail">
              <p className="m-0 whitespace-pre-wrap rounded-caseta border border-b4l-border bg-b4l-bg-soft px-3 py-2.5 text-corp text-b4l-text">
                {c.textMail || '(mailul n-are text)'}
              </p>
            </SectiunePanou>
          )}

          {poateScrie && (
            <SectiunePanou titlu="Acțiuni">
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setCorectez(true)}>
                  Corectează datele
                </Button>
                {c.stare !== 'nu_e_cerere' ? (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      if (
                        await confirma({
                          titlu: 'Nu e o cerere de concediu?',
                          mesaj:
                            'Dispare din listă și din calendar. O poți aduce înapoi din filtrul de stare.',
                          buton: 'Scoate-o din evidență',
                        })
                      )
                        salveaza.mutate({ stare: 'nu_e_cerere' })
                    }}
                  >
                    Scoate din evidență
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => salveaza.mutate({ stare: 'de_verificat' })}
                  >
                    Adu-o înapoi
                  </Button>
                )}
              </div>
            </SectiunePanou>
          )}
        </div>
      )}
      {c && (corectez || altOm) && (
        <Corectare
          c={c}
          doarOmul={altOm && !corectez}
          seSalveaza={salveaza.isPending}
          laInchidere={() => {
            setCorectez(false)
            setAltOm(false)
          }}
          laSalvare={(corp) => salveaza.mutate(corp)}
        />
      )}
    </PanouLateral>
  )
}

/** „Corectează datele" / „Alt om…": ce salvezi aici e verificat de tine, deci e înregistrată. */
function Corectare({
  c,
  doarOmul,
  seSalveaza,
  laInchidere,
  laSalvare,
}: {
  c: Cerere
  doarOmul: boolean
  seSalveaza: boolean
  laInchidere: () => void
  laSalvare: (corp: Record<string, unknown>) => void
}) {
  const oameni = useQuery<{ oameni: { id: number; name: string }[] }>({
    queryKey: ['concedii', 'oameni'],
    queryFn: async () => (await api.get('/concedii/oameni')).data,
    staleTime: 10 * 60_000,
  })
  const [userId, setUserId] = useState(c.om?.id ?? 0)
  const [tip, setTip] = useState<TipConcediu>(c.tip)
  const [detaliu, setDetaliu] = useState(c.tipDetaliu ?? '')
  const [start, setStart] = useState(c.dataStart ?? '')
  const [final, setFinal] = useState(c.dataFinal ?? '')
  const lipsa = !userId
    ? 'Alege angajatul.'
    : !start || !final
      ? 'Completează perioada.'
      : final < start
        ? 'Sfârșitul e înaintea începutului.'
        : null
  const camp =
    'w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text'

  return (
    <Modal
      open
      onClose={laInchidere}
      title={doarOmul ? 'Al cui e concediul?' : 'Corectează datele'}
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button
            disabled={!!lipsa}
            loading={seSalveaza}
            onClick={() =>
              laSalvare({
                userId,
                ...(doarOmul
                  ? {}
                  : {
                      tip,
                      tipDetaliu: tip === 'altele' ? detaliu.trim() || null : null,
                      dataStart: start,
                      dataFinal: final,
                    }),
                stare: 'inregistrata',
              })
            }
          >
            {lipsa ?? 'Salvează și înregistrează'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-corp">
        <p className="m-0 text-b4l-text-muted">
          Pe cerere scrie „{c.numeCitit}”. Ce salvezi aici e verificat de tine: cererea devine
          înregistrată.
        </p>
        <label className="grid gap-1">
          <span className="text-mic font-semibold text-b4l-text-muted">Angajat</span>
          <select
            value={userId}
            onChange={(e) => setUserId(Number(e.target.value))}
            className={camp}
          >
            <option value={0}>— alege —</option>
            {(oameni.data?.oameni ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        {!doarOmul && (
          <>
            <label className="grid gap-1">
              <span className="text-mic font-semibold text-b4l-text-muted">Tip</span>
              <select
                value={tip}
                onChange={(e) => setTip(e.target.value as TipConcediu)}
                className={camp}
              >
                {TIPURI_CONCEDIU.map((t) => (
                  <option key={t} value={t}>
                    {ETICHETE_TIP_CONCEDIU[t]}
                  </option>
                ))}
              </select>
            </label>
            {tip === 'altele' && (
              <label className="grid gap-1">
                <span className="text-mic font-semibold text-b4l-text-muted">
                  Ce fel de concediu
                </span>
                <input
                  value={detaliu}
                  onChange={(e) => setDetaliu(e.target.value)}
                  maxLength={120}
                  placeholder="ex. concediu de studii"
                  className={camp}
                />
              </label>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1">
                <span className="text-mic font-semibold text-b4l-text-muted">De la</span>
                <input
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className={camp}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-mic font-semibold text-b4l-text-muted">
                  Până la (inclusiv)
                </span>
                <input
                  type="date"
                  value={final}
                  onChange={(e) => setFinal(e.target.value)}
                  className={camp}
                />
              </label>
            </div>
            <p className="m-0 text-mic text-b4l-text-dim">
              Zilele lucrătoare se socotesc singure, fără weekend și fără sărbătorile legale.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}
